import React, { useState, useEffect } from 'react';
import { Table, Button, Switch, message, Tooltip } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { getS3Configs, updateS3Config } from '../utils/api';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';

interface S3Config {
  id: number;
  name: string;
  bucket_name: string;
  region: string;
  is_default: boolean;
  is_active: boolean;
}

const S3ConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<S3Config[]>([]);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    fetchConfigs();
  }, [token]);

  const fetchConfigs = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getS3Configs(token);
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.results || data);
      } else {
        message.error('Failed to fetch S3 configurations.');
      }
    } catch (error) {
      message.error('An error occurred while fetching S3 configurations.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (configId: number, field: 'is_active' | 'is_default', checked: boolean) => {
    if (!token) return;
    try {
      const response = await updateS3Config(configId, { [field]: checked }, token);
      if (response.ok) {
        message.success(`Configuration updated successfully.`);
        fetchConfigs(); // Refresh the list to show the new state
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Failed to update configuration.');
      }
    } catch (error) {
      message.error('An error occurred while updating the configuration.');
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Bucket', dataIndex: 'bucket_name', key: 'bucket_name' },
    { title: 'Region', dataIndex: 'region', key: 'region' },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: S3Config) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggle(record.id, 'is_active', checked)}
        />
      ),
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (isDefault: boolean, record: S3Config) => (
         <Switch
          checked={isDefault}
          onChange={(checked) => handleToggle(record.id, 'is_default', checked)}
        />
      ),
    },
     {
      title: 'Status',
      key: 'status',
      render: (_: any, record: S3Config) => (
        record.is_active && record.is_default
          ? <Tooltip title="This is the active default configuration"><CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '18px' }} /></Tooltip>
          : <Tooltip title="This is not the active default configuration"><CloseCircleTwoTone twoToneColor="#eb2f96" style={{ fontSize: '18px' }} /></Tooltip>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">S3 Configuration Management</h1>
        <Button onClick={fetchConfigs} loading={loading}>Refresh</Button>
      </div>
      <p className="mb-4">Here you can manage your S3 storage configurations. To create a directory for a product, at least one configuration must be set as both "Active" and "Default".</p>
      <Table columns={columns} dataSource={configs} rowKey="id" loading={loading} pagination={false} />
    </div>
  );
};

export default S3ConfigPage; 